/**
 * MediaFormatsList Component
 * Phase 3: Extracted from retailer-management.tsx
 * 
 * Displays media formats for a retailer with CRUD operations.
 * Uses server-side stats endpoint to avoid over-fetching stores.
 */

// TODO(RQv5): React Query v5 type mismatch.
// Known pre-existing issue from v5 migration.
// Not related to campaign creation, snapshots, or posting instructions.
// Tracked separately as frontend technical debt.

import { useState, useCallback, useRef } from "react";
import { useQuery, useMutation } from "@tanstack/react-query";
import { getJson, getJsonUnwrapped, asArray, unwrapPaginatedResponse, apiFetch } from "@/lib/http";
import { Plus, Edit, Trash2, Building2, FileText, ChevronDown, ChevronRight, X, Percent, Hash, Image as ImageIcon, Upload, Loader2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Checkbox } from "@/components/ui/checkbox";
import { Switch } from "@/components/ui/switch";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { useToast } from "@/hooks/use-toast";
import { apiRequest, queryClient } from "@/lib/queryClient";
import { cn } from "@/lib/utils";
import type { MediaFormat, InsertMediaFormat, StoreMediaRequirement, Store as StoreType } from "@shared/schema";
import { MAX_DURATION_CYCLES } from "@shared/removalDuration";

// Stats response type from the server
interface MediaFormatStats {
  mediaFormatId: number;
  mediaFormatName: string;
  totalStores: number;
  assignedStores: number;
  unassignedStores: number;
  totalUnits: number;
}

interface FormatArchivePreview {
  id: number;
  name: string;
  archivedAt: string | null;
  canDelete: boolean;
  activeStoreCount: number;
  storeNames: string[];
  campaignCount: number;
  evidenceCount: number;
  removalCount: number;
  confirmedDeliveryCount: number;
  packCount: number;
  otherReferenceCount: number;
}

type FormatDialogIntent = "loading" | "delete" | "blocked" | "archive" | "restore" | "error";

function plural(count: number, noun: string): string {
  return `${count} ${count === 1 ? noun : noun.endsWith("delivery") ? noun.slice(0, -1) + "ies" : noun + "s"}`;
}

function FormatUsageWarning({ preview, archive }: { preview: FormatArchivePreview; archive: boolean }) {
  const history = [
    preview.campaignCount > 0 ? plural(preview.campaignCount, "campaign") : null,
    preview.evidenceCount > 0 ? plural(preview.evidenceCount, "posting record") : null,
    preview.removalCount > 0 ? plural(preview.removalCount, "removal obligation") : null,
    preview.confirmedDeliveryCount > 0 ? plural(preview.confirmedDeliveryCount, "confirmed delivery") : null,
    preview.otherReferenceCount > 0 ? "other campaign history" : null,
  ].filter((item): item is string => Boolean(item));

  const listedStores = preview.storeNames.join(", ");
  const hiddenStoreCount = preview.activeStoreCount - preview.storeNames.length;

  return (
    <>
      {archive
        ? <p>Archive "{preview.name}" for this retailer?</p>
        : <p>"{preview.name}" cannot be deleted because it has these links:</p>}
      {preview.activeStoreCount > 0 && (
        <p>
          It is still assigned to {hiddenStoreCount > 0
            ? `${plural(preview.activeStoreCount, "store")}, including ${listedStores}`
            : listedStores || plural(preview.activeStoreCount, "store")}.
          {archive ? <> Archiving turns those store assignments off.</> : <> Remove those store assignments before deleting this format.</>}
        </p>
      )}
      {history.length > 0 && (
        <p>{archive ? "Past work stays in place" : "Campaign and operational records reference this format"}: {history.join(", ")}.</p>
      )}
      {preview.packCount > 0 && (
        <p>
          It is still used by {plural(preview.packCount, "pack")}. {archive ? "Those packs keep this format until you edit them." : "Remove it from those packs before deleting it."}
        </p>
      )}
      {archive ? (
        <p>Archiving hides this format from new campaigns, packs and store lists across {preview.activeStoreCount > 0 ? "all its stores" : "this retailer"}. Existing campaign history is preserved. You can restore it later; restoring does not turn store assignments back on.</p>
      ) : (
        <p>Nothing has been deleted.{history.length > 0 && " Historical records must be preserved."}</p>
      )}
    </>
  );
}

export interface MediaFormatsListProps {
  retailerId: number;
  retailerName: string;
}

interface FormatExampleFormValue {
  imageUrl: string;
  role: "good_example" | "bad_example" | "borderline";
  caption: string;
  previewUrl?: string;
}

function parseLegacyDimensions(dimensions: string | null | undefined): { widthMmInput: string; heightMmInput: string } {
  if (!dimensions?.trim()) {
    return { widthMmInput: "", heightMmInput: "" };
  }

  const numericParts = dimensions.match(/(\d+(?:\.\d+)?)/g) || [];
  if (numericParts.length < 2) {
    return { widthMmInput: "", heightMmInput: "" };
  }

  return {
    widthMmInput: String(Math.round(Number(numericParts[0]))),
    heightMmInput: String(Math.round(Number(numericParts[1]))),
  };
}

function createEmptyExample(): FormatExampleFormValue {
  return {
    imageUrl: "",
    role: "good_example",
    caption: "",
  };
}

function hasIncompleteStructuredDimensions(data: {
  widthMmInput?: string;
  heightMmInput?: string;
}): boolean {
  const hasWidth = Boolean(data.widthMmInput?.trim());
  const hasHeight = Boolean(data.heightMmInput?.trim());
  return hasWidth !== hasHeight;
}

function stringifyLineItems(items: string[] | null | undefined): string {
  return Array.isArray(items) ? items.join("\n") : "";
}

function parseLineItems(value: string | null | undefined): string[] {
  return (value || "")
    .split("\n")
    .map((item) => item.trim())
    .filter(Boolean);
}

function normalizeBookingMeta(
  bookingMeta: { bands?: number[]; absoluteLimitPerStore?: number } | null | undefined,
) {
  if (!bookingMeta || typeof bookingMeta !== "object") {
    return null;
  }

  const bands = Array.isArray(bookingMeta.bands) ? bookingMeta.bands : [];
  return {
    ...bookingMeta,
    bands,
  };
}

// Skeleton component for loading state
export function MediaFormatsListSkeleton() {
  return (
    <div className="space-y-4 animate-pulse">
      <div className="flex items-center justify-between">
        <div className="h-6 w-32 bg-gray-200 rounded" />
        <div className="h-9 w-36 bg-gray-200 rounded" />
      </div>
      <div className="grid gap-4">
        {[1, 2, 3].map((i) => (
          <Card key={i}>
            <CardContent className="p-4">
              <div className="flex items-start justify-between">
                <div className="flex-1 space-y-2">
                  <div className="h-5 w-40 bg-gray-200 rounded" />
                  <div className="h-4 w-64 bg-gray-100 rounded" />
                  <div className="flex gap-4">
                    <div className="h-5 w-16 bg-gray-100 rounded" />
                    <div className="h-5 w-20 bg-gray-100 rounded" />
                  </div>
                </div>
                <div className="flex gap-2">
                  <div className="h-8 w-8 bg-gray-200 rounded" />
                  <div className="h-8 w-8 bg-gray-200 rounded" />
                </div>
              </div>
            </CardContent>
          </Card>
        ))}
      </div>
    </div>
  );
}

export function MediaFormatsList({ retailerId, retailerName }: MediaFormatsListProps) {
  const { toast } = useToast();
  const [isCreateDialogOpen, setIsCreateDialogOpen] = useState(false);
  const [isEditDialogOpen, setIsEditDialogOpen] = useState(false);
  const [isDeleteDialogOpen, setIsDeleteDialogOpen] = useState(false);
  const [showArchived, setShowArchived] = useState(false);
  const [dialogIntent, setDialogIntent] = useState<FormatDialogIntent>("loading");
  const [archivePreview, setArchivePreview] = useState<FormatArchivePreview | null>(null);
  const [previewError, setPreviewError] = useState<string | null>(null);
  const [editingFormat, setEditingFormat] = useState<MediaFormat | null>(null);
  const [formatToDelete, setFormatToDelete] = useState<MediaFormat | null>(null);
  // Extended form data type for new booking fields
  interface ExtendedFormatFormData extends Omit<Partial<InsertMediaFormat>, "widthMm" | "heightMm"> {
    widthMmInput?: string;
    heightMmInput?: string;
    widthMm?: number | null;
    heightMm?: number | null;
    reviewerDescription?: string | null;
    placementGuidance?: string | null;
    visualCues?: string[];
    reviewChecklist?: string[];
    visualCuesText?: string;
    reviewChecklistText?: string;
    exampleImages?: FormatExampleFormValue[];
    capacityType?: "unlimited" | "limited";
    defaultDurationCyclesInput?: string;
    defaultDurationCycles?: number | null;
    bookingType?: "percentage" | "absolute" | null;
    bookingMeta?: { bands?: number[]; absoluteLimitPerStore?: number } | null;
  }

  const [formatFormData, setFormatFormData] = useState<ExtendedFormatFormData>({
    name: "",
    description: "",
    reviewerDescription: "",
    placementGuidance: "",
    visualCuesText: "",
    reviewChecklistText: "",
    photoUrl: "",
    exampleImages: [],
    widthMmInput: "",
    heightMmInput: "",
    type: "digital",
    // quantity_type is legacy and unused by packs; hidden for clarity.
    // It is still sent to the API for backwards compatibility but not shown in UI.
    quantityType: "fixed",
    capacityType: "unlimited",
    generatesRemovals: true,
    defaultDurationCyclesInput: "",
    bookingType: null,
    bookingMeta: null,
  });
  const [expandedFormats, setExpandedFormats] = useState<Set<number>>(new Set());
  const [newBandValue, setNewBandValue] = useState<string>("");
  const [photoPreviewUrl, setPhotoPreviewUrl] = useState<string>("");
  const [isUploadingPhoto, setIsUploadingPhoto] = useState(false);
  const [photoUploadMessage, setPhotoUploadMessage] = useState<string>("");
  const [uploadingExampleIndex, setUploadingExampleIndex] = useState<number | null>(null);
  const photoInputRef = useRef<HTMLInputElement | null>(null);
  const exampleInputRefs = useRef<Array<HTMLInputElement | null>>([]);

  const buildMediaFormatPayload = useCallback((data: ExtendedFormatFormData): ExtendedFormatFormData => ({
    ...data,
    description: data.description?.trim() ? data.description.trim() : null,
    reviewerDescription: data.reviewerDescription?.trim() ? data.reviewerDescription.trim() : null,
    placementGuidance: data.placementGuidance?.trim() ? data.placementGuidance.trim() : null,
    visualCues: parseLineItems(data.visualCuesText),
    reviewChecklist: parseLineItems(data.reviewChecklistText),
    photoUrl: data.photoUrl?.trim() ? data.photoUrl.trim() : null,
    exampleImages: (data.exampleImages || [])
      .map((example, index) => ({
        imageUrl: example.imageUrl.trim(),
        role: example.role,
        caption: example.caption.trim(),
        sortOrder: index,
      }))
      .filter((example) => example.imageUrl.length > 0)
      .slice(0, 4),
    widthMm: data.widthMmInput?.trim() ? Math.round(Number(data.widthMmInput.trim())) : null,
    heightMm: data.heightMmInput?.trim() ? Math.round(Number(data.heightMmInput.trim())) : null,
    defaultDurationCycles: data.defaultDurationCyclesInput?.trim()
      ? Number(data.defaultDurationCyclesInput.trim())
      : null,
    dimensions: (
      data.widthMmInput?.trim()
      && data.heightMmInput?.trim()
      && Number(data.widthMmInput) > 0
      && Number(data.heightMmInput) > 0
    ) ? `${Math.round(Number(data.widthMmInput.trim()))} x ${Math.round(Number(data.heightMmInput.trim()))} mm` : null,
  }), []);

  const uploadFormatImage = useCallback(async (file: File) => {
    const extension = file.name.includes(".") ? file.name.split(".").pop() : "jpg";
    const baseName = file.name.replace(/\.[^.]+$/, "").replace(/[^a-zA-Z0-9-_]+/g, "-").replace(/-+/g, "-").replace(/^-|-$/g, "") || "format-reference";
    const uploadFile = new File(
      [file],
      `${baseName}-${Date.now()}.${extension}`,
      { type: file.type || "image/jpeg" },
    );

    const formData = new FormData();
    formData.append("file", uploadFile);
    formData.append("bucket", "campaign-media");
    formData.append("folder", `campaign-media/format-references/retailer-${retailerId}`);

    const response = await apiRequest("/api/uploads/direct", "POST", formData);
    return response.json() as Promise<{ path: string; signedUrl?: string | null }>;
  }, [retailerId]);

  const toggleFormatExpansion = (formatId: number) => {
    setExpandedFormats(prev => {
      const newSet = new Set(prev);
      if (newSet.has(formatId)) {
        newSet.delete(formatId);
      } else {
        newSet.add(formatId);
      }
      return newSet;
    });
  };

  // Query media formats for this retailer (database-scoped by retailer_id)
  const { data: mediaFormatsData = [], isLoading } = useQuery<MediaFormat[]>({
    queryKey: ["/api/media-formats", retailerId, showArchived],
    queryFn: () => getJsonUnwrapped<MediaFormat>(
      `/api/media-formats?retailerId=${retailerId}${showArchived ? "&includeArchived=true" : ""}`,
    ),
  });
  const mediaFormats = asArray<MediaFormat>(mediaFormatsData);

  // [PHASE3] Use server-side stats instead of fetching all stores
  const { data: formatStatsData = [] } = useQuery<MediaFormatStats[]>({
    queryKey: ["/api/media-formats/stats", retailerId],
    queryFn: () => getJson<MediaFormatStats[]>(`/api/media-formats/stats?retailerId=${retailerId}`),
  });
  const formatStats = asArray<MediaFormatStats>(formatStatsData);

  // Query store media requirements for detailed expansion (only loaded when needed)
  const { data: storeMediaRequirementsData = [] } = useQuery<StoreMediaRequirement[]>({
    queryKey: ["/api/store-media-requirements", retailerId],
    queryFn: async () => {
      const res = await apiFetch(`/api/store-media-requirements?retailerId=${retailerId}`);
      if (!res.ok) {
        return [];
      }
      const json = await res.json();
      return unwrapPaginatedResponse<StoreMediaRequirement>(json);
    },
    // Only fetch when a format is expanded
    enabled: expandedFormats.size > 0,
  });
  const storeMediaRequirements = asArray<StoreMediaRequirement>(storeMediaRequirementsData);

  // Query stores only when expansion is needed
  const { data: stores = [] } = useQuery<StoreType[]>({
    queryKey: ["/api/stores", retailerId],
    queryFn: async () => {
      const res = await apiFetch(`/api/stores?retailerId=${retailerId}`);
      if (!res.ok) throw new Error(`${res.status}: ${res.statusText}`);
      return res.json();
    },
    // Only fetch when a format is expanded
    enabled: expandedFormats.size > 0,
  });

  // Get stats from server-side computed data
  const getFormatStats = (formatId: number) => {
    const serverStats = formatStats.find(s => s.mediaFormatId === formatId);
    if (serverStats) {
      return {
        storeCount: serverStats.assignedStores,
        totalUnits: serverStats.totalUnits,
        stores: [] as Array<{ storeName: string; quantity: number }>, // Will be loaded on expansion
      };
    }
    return { storeCount: 0, totalUnits: 0, stores: [] };
  };

  // Get detailed store info for expanded formats
  const getExpandedStoreDetails = (formatId: number) => {
    if (!expandedFormats.has(formatId)) return [];

    const requirements = storeMediaRequirements.filter(req => req.mediaFormatId === formatId && req.quantity > 0);
    return requirements.map(req => {
      const store = stores.find(s => s.id === req.storeId);
      return {
        storeName: store?.name || 'Unknown Store',
        quantity: req.quantity
      };
    });
  };

  // Helper functions for band management
  const handleAddBand = useCallback(() => {
    const value = parseInt(newBandValue, 10);
    if (isNaN(value) || value < 1 || value > 100) {
      toast({ title: "Invalid Band", description: "Band must be a number between 1 and 100", variant: "destructive" });
      return;
    }
    const currentBands = formatFormData.bookingMeta?.bands || [];
    if (currentBands.includes(value)) {
      toast({ title: "Duplicate Band", description: "This band value already exists", variant: "destructive" });
      return;
    }
    const newBands = [...currentBands, value].sort((a, b) => a - b);
    setFormatFormData({
      ...formatFormData,
      bookingMeta: { ...formatFormData.bookingMeta, bands: newBands },
    });
    setNewBandValue("");
  }, [newBandValue, formatFormData, toast]);

  const handleRemoveBand = useCallback((bandToRemove: number) => {
    const currentBands = formatFormData.bookingMeta?.bands || [];
    const newBands = currentBands.filter(b => b !== bandToRemove);
    setFormatFormData({
      ...formatFormData,
      bookingMeta: { ...formatFormData.bookingMeta, bands: newBands },
    });
  }, [formatFormData]);

  const handleCapacityTypeChange = useCallback((value: "unlimited" | "limited") => {
    if (value === "unlimited") {
      // Reset booking fields when switching to unlimited
      setFormatFormData({
        ...formatFormData,
        capacityType: value,
        bookingType: null,
        bookingMeta: null,
      });
    } else {
      // Set default booking type when switching to limited
      setFormatFormData({
        ...formatFormData,
        capacityType: value,
        bookingType: "percentage",
        bookingMeta: { bands: [25, 50, 75, 100] },
      });
    }
    setNewBandValue("");
  }, [formatFormData]);

  const handleBookingTypeChange = useCallback((value: "percentage" | "absolute") => {
    if (value === "percentage") {
      setFormatFormData({
        ...formatFormData,
        bookingType: value,
        bookingMeta: { bands: [25, 50, 75, 100] },
      });
    } else {
      setFormatFormData({
        ...formatFormData,
        bookingType: value,
        bookingMeta: { absoluteLimitPerStore: 1 },
      });
    }
    setNewBandValue("");
  }, [formatFormData]);

  const handleExampleImageChange = useCallback((
    index: number,
    field: keyof FormatExampleFormValue,
    value: string,
  ) => {
    setFormatFormData((current) => ({
      ...current,
      exampleImages: (current.exampleImages || []).map((example, exampleIndex) => (
        exampleIndex === index
          ? {
              ...example,
              [field]: value,
              ...(field === "imageUrl" ? { previewUrl: "" } : {}),
            }
          : example
      )),
    }));
  }, []);

  const handleAddExampleImage = useCallback(() => {
    setFormatFormData((current) => ({
      ...current,
      exampleImages: [...(current.exampleImages || []), createEmptyExample()].slice(0, 4),
    }));
  }, []);

  const handleRemoveExampleImage = useCallback((index: number) => {
    setFormatFormData((current) => ({
      ...current,
      exampleImages: (current.exampleImages || []).filter((_, exampleIndex) => exampleIndex !== index),
    }));
  }, []);

  const handlePhotoFileSelected = useCallback(async (file: File | null) => {
    if (!file) return;
    const localPreviewUrl = typeof URL !== "undefined" ? URL.createObjectURL(file) : "";
    setIsUploadingPhoto(true);
    setPhotoPreviewUrl(localPreviewUrl);
    setPhotoUploadMessage(`Uploading ${file.name}...`);
    try {
      const uploadResult = await uploadFormatImage(file);
      if (!uploadResult.path) {
        throw new Error("Upload did not return a storage path");
      }

      setFormatFormData((current) => ({
        ...current,
        photoUrl: uploadResult.path,
      }));
      setPhotoPreviewUrl(uploadResult.signedUrl || localPreviewUrl);
      setPhotoUploadMessage(`Reference image ready: ${file.name}`);
      toast({
        title: "Reference image uploaded",
        description: "The image is attached to this media format and will save with the form.",
      });
    } catch (error: any) {
      setPhotoUploadMessage(`Upload failed for ${file.name}`);
      toast({
        title: "Upload failed",
        description: error?.message || "Failed to upload reference image",
        variant: "destructive",
      });
    } finally {
      setIsUploadingPhoto(false);
      if (photoInputRef.current) {
        photoInputRef.current.value = "";
      }
    }
  }, [toast, uploadFormatImage]);

  const handleExampleFileSelected = useCallback(async (index: number, file: File | null) => {
    if (!file) return;
    setUploadingExampleIndex(index);
    try {
      const uploadResult = await uploadFormatImage(file);
      if (!uploadResult.path) {
        throw new Error("Upload did not return a storage path");
      }

      setFormatFormData((current) => ({
        ...current,
        exampleImages: (current.exampleImages || []).map((example, exampleIndex) => (
          exampleIndex === index
            ? {
                ...example,
                imageUrl: uploadResult.path,
                previewUrl: uploadResult.signedUrl || example.previewUrl,
              }
            : example
        )),
      }));
      toast({
        title: "Example image uploaded",
        description: "The curated example is attached to this format and will save with the form.",
      });
    } catch (error: any) {
      toast({
        title: "Upload failed",
        description: error?.message || "Failed to upload example image",
        variant: "destructive",
      });
    } finally {
      setUploadingExampleIndex(null);
      const input = exampleInputRefs.current[index];
      if (input) {
        input.value = "";
      }
    }
  }, [toast, uploadFormatImage]);

  const resetFormData = () => {
    setFormatFormData({
      name: "",
      description: "",
      reviewerDescription: "",
      placementGuidance: "",
      visualCuesText: "",
      reviewChecklistText: "",
      photoUrl: "",
      exampleImages: [],
      widthMmInput: "",
      heightMmInput: "",
      type: "digital",
      quantityType: "fixed",
      capacityType: "unlimited",
      generatesRemovals: true,
      defaultDurationCyclesInput: "",
      bookingType: null,
      bookingMeta: null,
    });
    setPhotoPreviewUrl("");
    setPhotoUploadMessage("");
    setNewBandValue("");
  };

  // Create media format mutation
  const createFormatMutation = useMutation({
    mutationFn: async (data: ExtendedFormatFormData) => {
      const payload = { ...buildMediaFormatPayload(data), retailerId };
      return apiRequest("/api/media-formats", "POST", payload);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/media-formats", retailerId] });
      queryClient.invalidateQueries({ queryKey: ["/api/media-formats/stats", retailerId] });
      setIsCreateDialogOpen(false);
      resetFormData();
      toast({ title: "Success", description: "Media format created successfully" });
    },
    onError: (error: any) => {
      const errorMessage =
        error?.response?.data?.error ||
        error?.response?.data?.message ||
        error?.message ||
        "Failed to create media format";
      toast({ title: "Error", description: errorMessage, variant: "destructive" });
    },
  });

  // Update media format mutation
  const updateFormatMutation = useMutation({
    mutationFn: async (data: { id: number; updates: ExtendedFormatFormData }) => {
      return apiRequest(`/api/media-formats/${data.id}`, "PATCH", buildMediaFormatPayload(data.updates));
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/media-formats"] });
      queryClient.invalidateQueries({ queryKey: ["/api/media-formats/stats", retailerId] });
      setIsEditDialogOpen(false);
      setEditingFormat(null);
      resetFormData();
      toast({ title: "Success", description: "Media format updated successfully" });
    },
    onError: () => {
      toast({ title: "Error", description: "Failed to update media format", variant: "destructive" });
    },
  });

  // Delete media format mutation
  const deleteFormatMutation = useMutation({
    mutationFn: async (id: number) => {
      return apiRequest(`/api/media-formats/${id}`, "DELETE");
    },
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ["/api/media-formats"], refetchType: 'active' });
      await queryClient.refetchQueries({ queryKey: ["/api/media-formats"] });
      queryClient.invalidateQueries({ queryKey: ["/api/media-formats/stats", retailerId] });
      setIsDeleteDialogOpen(false);
      setFormatToDelete(null);
      toast({ title: "Success", description: "Media format deleted successfully" });
    },
    onError: (error: any) => {
      const description = error?.data?.detail || error?.data?.error || error?.message || "Failed to delete media format";
      toast({ title: "Error", description, variant: "destructive" });
    },
  });

  const archiveFormatMutation = useMutation({
    mutationFn: async ({ id, archive }: { id: number; archive: boolean }) => {
      const response = await apiRequest(`/api/media-formats/${id}/archive`, "PATCH", { archive });
      return response.json();
    },
    onSuccess: async (_data, variables) => {
      await queryClient.invalidateQueries({ queryKey: ["/api/media-formats"], refetchType: "active" });
      await queryClient.refetchQueries({ queryKey: ["/api/media-formats"] });
      queryClient.invalidateQueries({ queryKey: ["/api/media-formats/stats", retailerId] });
      queryClient.invalidateQueries({ queryKey: ["/api/store-media-requirements", retailerId] });
      setIsDeleteDialogOpen(false);
      setFormatToDelete(null);
      setArchivePreview(null);
      toast({
        title: variables.archive ? "Format archived" : "Format restored",
        description: variables.archive
          ? "It is hidden from new campaigns, packs and store lists. Past campaign history is unchanged."
          : "It is back on the media format lists. Store assignments that were turned off stay off.",
      });
    },
    onError: (error: any) => {
      const description = error?.data?.error || error?.message || "Failed to archive media format";
      toast({ title: "Error", description, variant: "destructive" });
    },
  });

  const handleCreateFormat = () => {
    if (hasIncompleteStructuredDimensions(formatFormData)) {
      toast({
        title: "Dimensions incomplete",
        description: "Enter both width and height in mm, or leave both blank.",
        variant: "destructive",
      });
      return;
    }
    createFormatMutation.mutate(formatFormData);
  };

  const handleEditFormat = (format: MediaFormat) => {
    const legacyDimensions = parseLegacyDimensions(format.dimensions);
    setEditingFormat(format);
    const formatWithBooking = format as any;
    setFormatFormData({
      name: format.name,
      description: format.description || "",
      reviewerDescription: (format as any).reviewerDescription || "",
      placementGuidance: (format as any).placementGuidance || "",
      visualCuesText: stringifyLineItems((format as any).visualCues),
      reviewChecklistText: stringifyLineItems((format as any).reviewChecklist),
      photoUrl: (format as any).photoStoragePath || format.photoUrl || "",
      exampleImages: Array.isArray((format as any).exampleImages)
        ? (format as any).exampleImages.map((example: any) => ({
          imageUrl: example.storagePath || example.imageUrl || "",
          role: example.role === "bad_example" || example.role === "borderline" ? example.role : "good_example",
          caption: example.caption || "",
          previewUrl: example.imageUrl || "",
        }))
        : [],
      widthMmInput: format.widthMm != null ? String(format.widthMm) : legacyDimensions.widthMmInput,
      heightMmInput: format.heightMm != null ? String(format.heightMm) : legacyDimensions.heightMmInput,
      type: format.type || "digital",
      // quantity_type is legacy and unused by packs; hidden for clarity.
      quantityType: format.quantityType || "fixed",
      capacityType: formatWithBooking.capacityType || "unlimited",
      generatesRemovals: formatWithBooking.generatesRemovals !== false,
      defaultDurationCyclesInput: formatWithBooking.defaultDurationCycles != null
        ? String(formatWithBooking.defaultDurationCycles)
        : "",
      bookingType: formatWithBooking.bookingType || null,
      bookingMeta: normalizeBookingMeta(formatWithBooking.bookingMeta),
    });
    setPhotoPreviewUrl(format.photoUrl || "");
    setPhotoUploadMessage(format.photoUrl ? "Existing reference image loaded." : "");
    setNewBandValue("");
    setIsEditDialogOpen(true);
  };

  const handleUpdateFormat = () => {
    if (editingFormat) {
      if (hasIncompleteStructuredDimensions(formatFormData)) {
        toast({
          title: "Dimensions incomplete",
          description: "Enter both width and height in mm, or leave both blank.",
          variant: "destructive",
        });
        return;
      }
      updateFormatMutation.mutate({ id: editingFormat.id, updates: formatFormData });
    }
  };

  const handleFormatAction = async (format: MediaFormat, intent: "delete" | "archive") => {
    setFormatToDelete(format);
    setArchivePreview(null);
    setPreviewError(null);
    setDialogIntent("loading");
    setIsDeleteDialogOpen(true);
    try {
      const response = await apiRequest(`/api/media-formats/${format.id}/archive-preview`, "GET");
      const preview = await response.json() as FormatArchivePreview;
      setArchivePreview(preview);
      setDialogIntent(intent === "archive" ? "archive" : preview.canDelete ? "delete" : "blocked");
    } catch (error: any) {
      setPreviewError(error?.data?.error || error?.message || "Could not check media format usage");
      setDialogIntent("error");
    }
  };

  const handleRestoreFormat = (format: MediaFormat) => {
    setFormatToDelete(format);
    setArchivePreview(null);
    setPreviewError(null);
    setDialogIntent("restore");
    setIsDeleteDialogOpen(true);
  };

  const confirmDeleteFormat = () => {
    if (!formatToDelete) return;
    if (dialogIntent === "delete") {
      deleteFormatMutation.mutate(formatToDelete.id);
      return;
    }
    if (dialogIntent === "archive" || dialogIntent === "restore") {
      archiveFormatMutation.mutate({
        id: formatToDelete.id,
        archive: dialogIntent === "archive",
      });
    }
  };

  const renderExampleImageFields = (prefix: string) => (
    <div className="space-y-3">
      <div className="flex items-center justify-between">
        <div>
          <Label>Example Images</Label>
          <p className="text-xs text-muted-foreground mt-1">
            Curate up to 4 examples for the reviewer card.
          </p>
        </div>
        <Button
          type="button"
          variant="outline"
          size="sm"
          onClick={handleAddExampleImage}
          disabled={(formatFormData.exampleImages || []).length >= 4}
        >
          <Plus className="h-4 w-4 mr-1" />
          Add Example
        </Button>
      </div>
      {(formatFormData.exampleImages || []).length > 0 ? (
        <div className="space-y-4">
          {(formatFormData.exampleImages || []).map((example, index) => (
            <div key={`${prefix}-example-${index}`} className="rounded-md border p-3 space-y-3">
              <div className="flex items-start justify-between gap-3">
                <div className="grid flex-1 gap-3 md:grid-cols-[1fr_180px]">
                  <div>
                    <Label htmlFor={`${prefix}-example-url-${index}`}>Image URL</Label>
                    <Input
                      id={`${prefix}-example-url-${index}`}
                      placeholder="Paste an example image URL"
                      value={example.imageUrl}
                      onChange={(e) => handleExampleImageChange(index, "imageUrl", e.target.value)}
                    />
                    <div className="mt-2 flex items-center gap-2">
                      <input
                        ref={(node) => {
                          exampleInputRefs.current[index] = node;
                        }}
                        type="file"
                        accept="image/png,image/jpeg"
                        className="hidden"
                        onChange={(e) => {
                          void handleExampleFileSelected(index, e.target.files?.[0] || null);
                        }}
                      />
                      <Button
                        type="button"
                        variant="outline"
                        size="sm"
                        onClick={() => exampleInputRefs.current[index]?.click()}
                        disabled={uploadingExampleIndex === index}
                      >
                        {uploadingExampleIndex === index ? (
                          <Loader2 className="h-4 w-4 mr-1 animate-spin" />
                        ) : (
                          <Upload className="h-4 w-4 mr-1" />
                        )}
                        Upload
                      </Button>
                      <span className="text-xs text-muted-foreground">
                        JPG or PNG, up to 15MB.
                      </span>
                    </div>
                  </div>
                  <div>
                    <Label htmlFor={`${prefix}-example-role-${index}`}>Role</Label>
                    <Select
                      value={example.role}
                      onValueChange={(value: "good_example" | "bad_example" | "borderline") => handleExampleImageChange(index, "role", value)}
                    >
                      <SelectTrigger id={`${prefix}-example-role-${index}`}>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent className="z-[70]">
                        <SelectItem value="good_example">Good example</SelectItem>
                        <SelectItem value="borderline">Borderline</SelectItem>
                        <SelectItem value="bad_example">Bad example</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                </div>
                <Button
                  type="button"
                  variant="ghost"
                  size="icon"
                  onClick={() => handleRemoveExampleImage(index)}
                >
                  <Trash2 className="h-4 w-4" />
                </Button>
              </div>
              <div>
                <Label htmlFor={`${prefix}-example-caption-${index}`}>Caption</Label>
                <Input
                  id={`${prefix}-example-caption-${index}`}
                  placeholder="Optional caption for reviewers"
                  value={example.caption}
                  onChange={(e) => handleExampleImageChange(index, "caption", e.target.value)}
                />
              </div>
              {(example.previewUrl || example.imageUrl) ? (
                <div className="flex items-center gap-3 rounded-md border p-3">
                  <img
                    src={example.previewUrl || example.imageUrl}
                    alt={`Example ${index + 1}`}
                    className="h-14 w-14 rounded-md border object-cover"
                  />
                  <div className="text-xs text-muted-foreground">
                    This example will appear on the reviewer Expected Format card.
                  </div>
                </div>
              ) : null}
            </div>
          ))}
        </div>
      ) : (
        <div className="flex items-center gap-2 text-xs text-muted-foreground">
          <ImageIcon className="h-3.5 w-3.5" />
          No curated examples yet. Add a few strong references to improve reviewer consistency.
        </div>
      )}
    </div>
  );

  if (isLoading) return <MediaFormatsListSkeleton />;

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <h3 className="text-lg font-semibold">Media Formats</h3>
        <Button onClick={() => setIsCreateDialogOpen(true)}>
          <Plus className="h-4 w-4 mr-2" />
          Add Media Format
        </Button>
      </div>

      <div className="flex items-center space-x-2">
        <Checkbox
          id="show-archived-formats"
          checked={showArchived}
          onCheckedChange={(checked) => setShowArchived(!!checked)}
        />
        <label
          htmlFor="show-archived-formats"
          className="text-sm font-medium leading-none cursor-pointer"
        >
          Show archived formats
        </label>
      </div>

      <div className="grid gap-4">
        {mediaFormats.map((format) => {
          const stats = getFormatStats(format.id);
          const expandedDetails = getExpandedStoreDetails(format.id);
          return (
            <Card key={format.id} className={format.archivedAt ? "opacity-60" : ""}>
              <CardContent className="p-4">
                <div className="flex items-start justify-between">
                  <div className="flex-1">
                    <h4 className="font-semibold">{format.name}</h4>
                    {format.description && (
                      <p className="text-sm text-muted-foreground mt-1">{format.description}</p>
                    )}
                    <div className="flex items-center gap-4 mt-2">
                      <Badge variant={(format as any).capacityType === 'limited' || stats.storeCount === 0 || stats.totalUnits === 0 ? 'destructive' : 'default'}>
                        {(format as any).capacityType === 'limited' || stats.storeCount === 0 || stats.totalUnits === 0 ? 'Limited' : 'Unlimited'}
                      </Badge>
                      {format.archivedAt && (
                        <Badge variant="outline">Archived</Badge>
                      )}
                      {(format as any).generatesRemovals === false && (
                        <Badge variant="outline">No removals</Badge>
                      )}
                      {(format as any).generatesRemovals !== false && (
                        <Badge variant="secondary">
                          {(format as any).defaultDurationCycles ?? 1} cycle{((format as any).defaultDurationCycles ?? 1) === 1 ? "" : "s"}
                        </Badge>
                      )}
                      <div className="flex items-center gap-4 text-sm text-muted-foreground">
                        <span className="flex items-center gap-1">
                          <Building2 className="h-3 w-3" />
                          {stats.storeCount} stores
                        </span>
                        <span className="flex items-center gap-1">
                          <FileText className="h-3 w-3" />
                          {stats.totalUnits} units
                        </span>
                      </div>
                    </div>
                    {stats.storeCount > 0 && (
                      <div className="mt-2">
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => toggleFormatExpansion(format.id)}
                          className="h-6 px-2 text-xs text-muted-foreground hover:text-foreground"
                        >
                          {expandedFormats.has(format.id) ? (
                            <>
                              <ChevronDown className="h-3 w-3 mr-1" />
                              Hide store details
                            </>
                          ) : (
                            <>
                              <ChevronRight className="h-3 w-3 mr-1" />
                              Show store details
                            </>
                          )}
                        </Button>
                        {expandedFormats.has(format.id) && expandedDetails.length > 0 && (
                          <div className="mt-1 text-xs text-muted-foreground">
                            Available at: {expandedDetails.map(s => `${s.storeName} (${s.quantity})`).join(', ')}
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                  <div className="flex items-center gap-2">
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => handleEditFormat(format)}
                    >
                      <Edit className="h-4 w-4" />
                    </Button>
                    {format.archivedAt ? (
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => handleRestoreFormat(format)}
                      >
                        Restore
                      </Button>
                    ) : (
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => handleFormatAction(format, "archive")}
                        aria-label={`Archive ${format.name}`}
                      >
                        Archive
                      </Button>
                    )}
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => handleFormatAction(format, "delete")}
                      title="Delete media format"
                      aria-label={`Delete ${format.name}`}
                    >
                      <Trash2 className="h-4 w-4" />
                    </Button>
                  </div>
                </div>
              </CardContent>
            </Card>
          );
        })}
      </div>

      {/* Create Dialog */}
      <Dialog open={isCreateDialogOpen} onOpenChange={setIsCreateDialogOpen}>
        <DialogContent className={cn("bg-white max-h-[90vh] overflow-y-auto")}>
          <DialogHeader>
            <DialogTitle>Create Media Format</DialogTitle>
            <DialogDescription>
              Create a new media format for {retailerName}
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <Label htmlFor="name">Format Name</Label>
              <Input
                id="name"
                placeholder="Enter format name"
                value={formatFormData.name || ""}
                onChange={(e) => setFormatFormData({ ...formatFormData, name: e.target.value })}
              />
            </div>
            <div>
              <Label htmlFor="description">Description</Label>
              <Textarea
                id="description"
                placeholder="Enter format description"
                value={formatFormData.description || ""}
                onChange={(e) => setFormatFormData({ ...formatFormData, description: e.target.value })}
              />
            </div>
            <div>
              <Label htmlFor="reviewerDescription">Reviewer Description</Label>
              <Textarea
                id="reviewerDescription"
                placeholder="Short reviewer-facing description of what this format should look like"
                value={formatFormData.reviewerDescription || ""}
                onChange={(e) => setFormatFormData({ ...formatFormData, reviewerDescription: e.target.value })}
              />
            </div>
            <div>
              <Label htmlFor="placementGuidance">Placement Guidance</Label>
              <Textarea
                id="placementGuidance"
                placeholder="Where should this format be mounted or positioned in store?"
                value={formatFormData.placementGuidance || ""}
                onChange={(e) => setFormatFormData({ ...formatFormData, placementGuidance: e.target.value })}
              />
            </div>
            <div>
              <Label htmlFor="visualCuesText">Visual Cues</Label>
              <Textarea
                id="visualCuesText"
                placeholder={"One cue per line\nBranded vertical panel\nMounted on aisle-facing bay edge"}
                value={formatFormData.visualCuesText || ""}
                onChange={(e) => setFormatFormData({ ...formatFormData, visualCuesText: e.target.value })}
              />
            </div>
            <div>
              <Label htmlFor="reviewChecklistText">Review Checklist</Label>
              <Textarea
                id="reviewChecklistText"
                placeholder={"One check per line\nCorrect format visible\nMounted in expected location"}
                value={formatFormData.reviewChecklistText || ""}
                onChange={(e) => setFormatFormData({ ...formatFormData, reviewChecklistText: e.target.value })}
              />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label htmlFor="widthMm">Width (mm)</Label>
                <Input
                  id="widthMm"
                  type="number"
                  min="1"
                  step="1"
                  placeholder="1200"
                  value={formatFormData.widthMmInput || ""}
                  onChange={(e) => setFormatFormData({ ...formatFormData, widthMmInput: e.target.value })}
                />
              </div>
              <div>
                <Label htmlFor="heightMm">Height (mm)</Label>
                <Input
                  id="heightMm"
                  type="number"
                  min="1"
                  step="1"
                  placeholder="1800"
                  value={formatFormData.heightMmInput || ""}
                  onChange={(e) => setFormatFormData({ ...formatFormData, heightMmInput: e.target.value })}
                />
              </div>
            </div>
            <p className="text-xs text-muted-foreground">
              Enter both width and height in millimetres, or leave both blank.
            </p>
            <div className="flex items-center justify-between gap-4">
              <div className="space-y-1">
                <Label htmlFor="generatesRemovals">Generate removals for this format</Label>
                <p className="text-xs text-muted-foreground">
                  When off, cycle-lock will not create removal obligations or tasks for this format.
                </p>
              </div>
              <Switch
                id="generatesRemovals"
                checked={formatFormData.generatesRemovals !== false}
                onCheckedChange={(checked) => setFormatFormData({ ...formatFormData, generatesRemovals: checked })}
              />
            </div>
            {formatFormData.generatesRemovals !== false && (
              <div>
                <Label htmlFor="defaultDurationCycles">Default duration (cycles)</Label>
                <Input
                  id="defaultDurationCycles"
                  type="number"
                  min={1}
                  max={MAX_DURATION_CYCLES}
                  step={1}
                  placeholder="1 (system default)"
                  value={formatFormData.defaultDurationCyclesInput ?? ""}
                  onChange={(event) => setFormatFormData({
                    ...formatFormData,
                    defaultDurationCyclesInput: event.target.value,
                  })}
                />
                <p className="text-xs text-muted-foreground mt-1">
                  How many cycles media in this format stays installed before removal is due (1–{MAX_DURATION_CYCLES}). Leave blank for 1 cycle.
                </p>
              </div>
            )}
            <div className="space-y-2">
              <Label htmlFor="photoUrl">Reference Image URL</Label>
              <Input
                id="photoUrl"
                placeholder="Paste a reference image URL"
                value={formatFormData.photoUrl || ""}
                onChange={(e) => {
                  setPhotoPreviewUrl("");
                  setFormatFormData({ ...formatFormData, photoUrl: e.target.value });
                }}
              />
              <div className="flex items-center gap-2">
                <input
                  ref={photoInputRef}
                  type="file"
                  accept="image/png,image/jpeg"
                  className="hidden"
                  onChange={(e) => {
                    void handlePhotoFileSelected(e.target.files?.[0] || null);
                  }}
                />
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  onClick={() => photoInputRef.current?.click()}
                  disabled={isUploadingPhoto}
                >
                  {isUploadingPhoto ? (
                    <Loader2 className="h-4 w-4 mr-1 animate-spin" />
                  ) : (
                    <Upload className="h-4 w-4 mr-1" />
                  )}
                  Upload Reference
                </Button>
                <span className="text-xs text-muted-foreground">JPG or PNG, up to 15MB.</span>
              </div>
              {photoUploadMessage ? (
                <div className="text-xs text-muted-foreground">{photoUploadMessage}</div>
              ) : null}
              {(photoPreviewUrl || formatFormData.photoUrl) ? (
                <div className="flex items-center gap-3 rounded-md border p-3">
                  <img
                    src={photoPreviewUrl || formatFormData.photoUrl || ""}
                    alt="Reference preview"
                    className="h-14 w-14 rounded-md border object-cover"
                  />
                  <div className="text-xs text-muted-foreground">
                    This image will be used as the primary retailer format reference.
                  </div>
                </div>
              ) : (
                <div className="flex items-center gap-2 text-xs text-muted-foreground">
                  <ImageIcon className="h-3.5 w-3.5" />
                  Add a reference image URL to improve reviewer guidance and format examples.
                </div>
              )}
            </div>
            {renderExampleImageFields("create")}
            <div>
              <Label htmlFor="type">Type</Label>
              <Select
                value={formatFormData.type || "digital"}
                onValueChange={(value: "digital" | "print" | "audio" | "video") =>
                  setFormatFormData({ ...formatFormData, type: value })
                }
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent className="z-[70]">
                  <SelectItem value="digital">Digital</SelectItem>
                  <SelectItem value="print">Print</SelectItem>
                  <SelectItem value="audio">Audio</SelectItem>
                  <SelectItem value="video">Video</SelectItem>
                </SelectContent>
              </Select>
            </div>
            {/* quantity_type is legacy and unused by packs; hidden for clarity. */}

            {/* Capacity Type Selector */}
            <div>
              <Label htmlFor="capacityType">Capacity Type</Label>
              <Select
                value={formatFormData.capacityType || "unlimited"}
                onValueChange={handleCapacityTypeChange}
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent className="z-[70]">
                  <SelectItem value="unlimited">Unlimited Capacity</SelectItem>
                  <SelectItem value="limited">Limited Capacity (PopAd)</SelectItem>
                </SelectContent>
              </Select>
              <p className="text-xs text-muted-foreground mt-1">
                {formatFormData.capacityType === "limited"
                  ? "Limited capacity formats have per-store availability constraints (e.g., trolley media)"
                  : "Unlimited capacity formats can be booked without per-store limits"}
              </p>
            </div>

            {/* Booking Type - only shown for limited capacity */}
            {formatFormData.capacityType === "limited" && (
              <>
                <div>
                  <Label htmlFor="bookingType">Booking Type</Label>
                  <Select
                    value={formatFormData.bookingType || "percentage"}
                    onValueChange={handleBookingTypeChange}
                  >
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent className="z-[70]">
                      <SelectItem value="percentage">
                        <div className="flex items-center gap-2">
                          <Percent className="h-4 w-4" />
                          <span>Percentage Bands</span>
                        </div>
                      </SelectItem>
                      <SelectItem value="absolute">
                        <div className="flex items-center gap-2">
                          <Hash className="h-4 w-4" />
                          <span>Absolute Limit</span>
                        </div>
                      </SelectItem>
                    </SelectContent>
                  </Select>
                  <p className="text-xs text-muted-foreground mt-1">
                    {formatFormData.bookingType === "percentage"
                      ? "Define percentage bands (e.g., 25%, 50%, 75%, 100%) that campaigns can book"
                      : "Set a fixed number of units that can be booked per store"}
                  </p>
                </div>

                {/* Band Editor - shown for percentage booking */}
                {formatFormData.bookingType === "percentage" && (
                  <div>
                    <Label>Booking Bands</Label>
                    <div className="flex flex-wrap gap-2 mt-2 mb-2">
                      {(formatFormData.bookingMeta?.bands || []).map((band) => (
                        <Badge key={band} variant="secondary" className="flex items-center gap-1">
                          {band}%
                          <button
                            type="button"
                            onClick={() => handleRemoveBand(band)}
                            className="ml-1 hover:text-destructive"
                          >
                            <X className="h-3 w-3" />
                          </button>
                        </Badge>
                      ))}
                    </div>
                    <div className="flex gap-2">
                      <Input
                        type="number"
                        placeholder="Add band (1-100)"
                        value={newBandValue}
                        onChange={(e) => setNewBandValue(e.target.value)}
                        min={1}
                        max={100}
                        className="w-32"
                        onKeyDown={(e) => {
                          if (e.key === 'Enter') {
                            e.preventDefault();
                            handleAddBand();
                          }
                        }}
                      />
                      <Button type="button" variant="outline" size="sm" onClick={handleAddBand}>
                        <Plus className="h-4 w-4" />
                      </Button>
                    </div>
                    <p className="text-xs text-muted-foreground mt-1">
                      Enter a percentage value and click + or press Enter to add
                    </p>
                  </div>
                )}

                {/* Absolute Limit Field - shown for absolute booking */}
                {formatFormData.bookingType === "absolute" && (
                  <div>
                    <Label htmlFor="absoluteLimit">Absolute Limit Per Store</Label>
                    <Input
                      id="absoluteLimit"
                      type="number"
                      min={1}
                      value={formatFormData.bookingMeta?.absoluteLimitPerStore || 1}
                      onChange={(e) => setFormatFormData({
                        ...formatFormData,
                        bookingMeta: { absoluteLimitPerStore: parseInt(e.target.value, 10) || 1 },
                      })}
                    />
                    <p className="text-xs text-muted-foreground mt-1">
                      Maximum number of units that can be booked per store
                    </p>
                  </div>
                )}
              </>
            )}
          </div>
          <div className="flex justify-end space-x-2 mt-6">
            <Button variant="outline" onClick={() => { setIsCreateDialogOpen(false); resetFormData(); }}>
              Cancel
            </Button>
            <Button
              onClick={handleCreateFormat}
              disabled={!formatFormData.name || createFormatMutation.isPending}
            >
              {createFormatMutation.isPending ? "Creating..." : "Create Format"}
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Edit Dialog */}
      <Dialog open={isEditDialogOpen} onOpenChange={setIsEditDialogOpen}>
        <DialogContent className={cn("bg-white max-h-[90vh] overflow-y-auto")}>
          <DialogHeader>
            <DialogTitle>Edit Media Format</DialogTitle>
            <DialogDescription>
              Update the media format details
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <Label htmlFor="edit-name">Format Name</Label>
              <Input
                id="edit-name"
                value={formatFormData.name || ""}
                onChange={(e) => setFormatFormData({ ...formatFormData, name: e.target.value })}
              />
            </div>
            <div>
              <Label htmlFor="edit-description">Description</Label>
              <Textarea
                id="edit-description"
                value={formatFormData.description || ""}
                onChange={(e) => setFormatFormData({ ...formatFormData, description: e.target.value })}
              />
            </div>
            <div>
              <Label htmlFor="edit-reviewerDescription">Reviewer Description</Label>
              <Textarea
                id="edit-reviewerDescription"
                placeholder="Short reviewer-facing description of what this format should look like"
                value={formatFormData.reviewerDescription || ""}
                onChange={(e) => setFormatFormData({ ...formatFormData, reviewerDescription: e.target.value })}
              />
            </div>
            <div>
              <Label htmlFor="edit-placementGuidance">Placement Guidance</Label>
              <Textarea
                id="edit-placementGuidance"
                placeholder="Where should this format be mounted or positioned in store?"
                value={formatFormData.placementGuidance || ""}
                onChange={(e) => setFormatFormData({ ...formatFormData, placementGuidance: e.target.value })}
              />
            </div>
            <div>
              <Label htmlFor="edit-visualCuesText">Visual Cues</Label>
              <Textarea
                id="edit-visualCuesText"
                placeholder={"One cue per line\nBranded vertical panel\nMounted on aisle-facing bay edge"}
                value={formatFormData.visualCuesText || ""}
                onChange={(e) => setFormatFormData({ ...formatFormData, visualCuesText: e.target.value })}
              />
            </div>
            <div>
              <Label htmlFor="edit-reviewChecklistText">Review Checklist</Label>
              <Textarea
                id="edit-reviewChecklistText"
                placeholder={"One check per line\nCorrect format visible\nMounted in expected location"}
                value={formatFormData.reviewChecklistText || ""}
                onChange={(e) => setFormatFormData({ ...formatFormData, reviewChecklistText: e.target.value })}
              />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label htmlFor="edit-widthMm">Width (mm)</Label>
                <Input
                  id="edit-widthMm"
                  type="number"
                  min="1"
                  step="1"
                  placeholder="1200"
                  value={formatFormData.widthMmInput || ""}
                  onChange={(e) => setFormatFormData({ ...formatFormData, widthMmInput: e.target.value })}
                />
              </div>
              <div>
                <Label htmlFor="edit-heightMm">Height (mm)</Label>
                <Input
                  id="edit-heightMm"
                  type="number"
                  min="1"
                  step="1"
                  placeholder="1800"
                  value={formatFormData.heightMmInput || ""}
                  onChange={(e) => setFormatFormData({ ...formatFormData, heightMmInput: e.target.value })}
                />
              </div>
            </div>
            <p className="text-xs text-muted-foreground">
              Enter both width and height in millimetres, or leave both blank.
            </p>
            <div className="flex items-center justify-between gap-4">
              <div className="space-y-1">
                <Label htmlFor="edit-generatesRemovals">Generate removals for this format</Label>
                <p className="text-xs text-muted-foreground">
                  When off, cycle-lock will not create removal obligations or tasks for this format.
                </p>
              </div>
              <Switch
                id="edit-generatesRemovals"
                checked={formatFormData.generatesRemovals !== false}
                onCheckedChange={(checked) => setFormatFormData({ ...formatFormData, generatesRemovals: checked })}
              />
            </div>
            {formatFormData.generatesRemovals !== false && (
              <div>
                <Label htmlFor="edit-defaultDurationCycles">Default duration (cycles)</Label>
                <Input
                  id="edit-defaultDurationCycles"
                  type="number"
                  min={1}
                  max={MAX_DURATION_CYCLES}
                  step={1}
                  placeholder="1 (system default)"
                  value={formatFormData.defaultDurationCyclesInput ?? ""}
                  onChange={(event) => setFormatFormData({
                    ...formatFormData,
                    defaultDurationCyclesInput: event.target.value,
                  })}
                />
                <p className="text-xs text-muted-foreground mt-1">
                  How many cycles media in this format stays installed before removal is due (1–{MAX_DURATION_CYCLES}). Leave blank for 1 cycle.
                </p>
              </div>
            )}
            <div className="space-y-2">
              <Label htmlFor="edit-photoUrl">Reference Image URL</Label>
              <Input
                id="edit-photoUrl"
                placeholder="Paste a reference image URL"
                value={formatFormData.photoUrl || ""}
                onChange={(e) => {
                  setPhotoPreviewUrl("");
                  setFormatFormData({ ...formatFormData, photoUrl: e.target.value });
                }}
              />
              <div className="flex items-center gap-2">
                <input
                  ref={photoInputRef}
                  type="file"
                  accept="image/png,image/jpeg"
                  className="hidden"
                  onChange={(e) => {
                    void handlePhotoFileSelected(e.target.files?.[0] || null);
                  }}
                />
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  onClick={() => photoInputRef.current?.click()}
                  disabled={isUploadingPhoto}
                >
                  {isUploadingPhoto ? (
                    <Loader2 className="h-4 w-4 mr-1 animate-spin" />
                  ) : (
                    <Upload className="h-4 w-4 mr-1" />
                  )}
                  Upload Reference
                </Button>
                <span className="text-xs text-muted-foreground">JPG or PNG, up to 15MB.</span>
              </div>
              {photoUploadMessage ? (
                <div className="text-xs text-muted-foreground">{photoUploadMessage}</div>
              ) : null}
              {(photoPreviewUrl || formatFormData.photoUrl) ? (
                <div className="flex items-center gap-3 rounded-md border p-3">
                  <img
                    src={photoPreviewUrl || formatFormData.photoUrl || ""}
                    alt="Reference preview"
                    className="h-14 w-14 rounded-md border object-cover"
                  />
                  <div className="text-xs text-muted-foreground">
                    This image will be used as the primary retailer format reference.
                  </div>
                </div>
              ) : (
                <div className="flex items-center gap-2 text-xs text-muted-foreground">
                  <ImageIcon className="h-3.5 w-3.5" />
                  Add a reference image URL to improve reviewer guidance and format examples.
                </div>
              )}
            </div>
            {renderExampleImageFields("edit")}
            <div>
              <Label htmlFor="edit-type">Type</Label>
              <Select
                value={formatFormData.type || "digital"}
                onValueChange={(value: "digital" | "print" | "audio" | "video") =>
                  setFormatFormData({ ...formatFormData, type: value })
                }
              >
                <SelectTrigger>
                  <SelectValue placeholder="Select type" />
                </SelectTrigger>
                <SelectContent className="z-[70]">
                  <SelectItem value="digital">Digital</SelectItem>
                  <SelectItem value="print">Print</SelectItem>
                  <SelectItem value="audio">Audio</SelectItem>
                  <SelectItem value="video">Video</SelectItem>
                </SelectContent>
              </Select>
            </div>
            {/* quantity_type is legacy and unused by packs; hidden for clarity. */}

            {/* Capacity Type Selector */}
            <div>
              <Label htmlFor="edit-capacityType">Capacity Type</Label>
              <Select
                value={formatFormData.capacityType || "unlimited"}
                onValueChange={handleCapacityTypeChange}
              >
                <SelectTrigger>
                  <SelectValue placeholder="Select capacity type" />
                </SelectTrigger>
                <SelectContent className="z-[70]">
                  <SelectItem value="unlimited">Unlimited Capacity</SelectItem>
                  <SelectItem value="limited">Limited Capacity (PopAd)</SelectItem>
                </SelectContent>
              </Select>
              <p className="text-xs text-muted-foreground mt-1">
                {formatFormData.capacityType === "limited"
                  ? "Limited capacity formats have per-store availability constraints (e.g., trolley media)"
                  : "Unlimited capacity formats can be booked without per-store limits"}
              </p>
            </div>

            {/* Booking Type - only shown for limited capacity */}
            {formatFormData.capacityType === "limited" && (
              <>
                <div>
                  <Label htmlFor="edit-bookingType">Booking Type</Label>
                  <Select
                    value={formatFormData.bookingType || "percentage"}
                    onValueChange={handleBookingTypeChange}
                  >
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent className="z-[70]">
                      <SelectItem value="percentage">
                        <div className="flex items-center gap-2">
                          <Percent className="h-4 w-4" />
                          <span>Percentage Bands</span>
                        </div>
                      </SelectItem>
                      <SelectItem value="absolute">
                        <div className="flex items-center gap-2">
                          <Hash className="h-4 w-4" />
                          <span>Absolute Limit</span>
                        </div>
                      </SelectItem>
                    </SelectContent>
                  </Select>
                  <p className="text-xs text-muted-foreground mt-1">
                    {formatFormData.bookingType === "percentage"
                      ? "Define percentage bands (e.g., 25%, 50%, 75%, 100%) that campaigns can book"
                      : "Set a fixed number of units that can be booked per store"}
                  </p>
                </div>

                {/* Band Editor - shown for percentage booking */}
                {formatFormData.bookingType === "percentage" && (
                  <div>
                    <Label>Booking Bands</Label>
                    <div className="flex flex-wrap gap-2 mt-2 mb-2">
                      {(formatFormData.bookingMeta?.bands || []).map((band) => (
                        <Badge key={band} variant="secondary" className="flex items-center gap-1">
                          {band}%
                          <button
                            type="button"
                            onClick={() => handleRemoveBand(band)}
                            className="ml-1 hover:text-destructive"
                          >
                            <X className="h-3 w-3" />
                          </button>
                        </Badge>
                      ))}
                    </div>
                    <div className="flex gap-2">
                      <Input
                        type="number"
                        placeholder="Add band (1-100)"
                        value={newBandValue}
                        onChange={(e) => setNewBandValue(e.target.value)}
                        min={1}
                        max={100}
                        className="w-32"
                        onKeyDown={(e) => {
                          if (e.key === 'Enter') {
                            e.preventDefault();
                            handleAddBand();
                          }
                        }}
                      />
                      <Button type="button" variant="outline" size="sm" onClick={handleAddBand}>
                        <Plus className="h-4 w-4" />
                      </Button>
                    </div>
                    <p className="text-xs text-muted-foreground mt-1">
                      Enter a percentage value and click + or press Enter to add
                    </p>
                  </div>
                )}

                {/* Absolute Limit Field - shown for absolute booking */}
                {formatFormData.bookingType === "absolute" && (
                  <div>
                    <Label htmlFor="edit-absoluteLimit">Absolute Limit Per Store</Label>
                    <Input
                      id="edit-absoluteLimit"
                      type="number"
                      min={1}
                      value={formatFormData.bookingMeta?.absoluteLimitPerStore || 1}
                      onChange={(e) => setFormatFormData({
                        ...formatFormData,
                        bookingMeta: { absoluteLimitPerStore: parseInt(e.target.value, 10) || 1 },
                      })}
                    />
                    <p className="text-xs text-muted-foreground mt-1">
                      Maximum number of units that can be booked per store
                    </p>
                  </div>
                )}
              </>
            )}
          </div>
          <div className="flex justify-end space-x-2 mt-6">
            <Button variant="outline" onClick={() => { setIsEditDialogOpen(false); resetFormData(); }}>
              Cancel
            </Button>
            <Button
              onClick={handleUpdateFormat}
              disabled={!formatFormData.name || updateFormatMutation.isPending}
            >
              {updateFormatMutation.isPending ? "Updating..." : "Update Format"}
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Delete and archive are separate actions. */}
      <AlertDialog open={isDeleteDialogOpen} onOpenChange={setIsDeleteDialogOpen}>
        <AlertDialogContent className={cn("bg-white")}>
          <AlertDialogHeader>
            <AlertDialogTitle>
              {dialogIntent === "archive"
                ? "Archive this format?"
                : dialogIntent === "restore"
                  ? "Restore this format?"
                  : dialogIntent === "blocked"
                    ? "Cannot delete media format"
                    : "Delete media format"}
            </AlertDialogTitle>
            <AlertDialogDescription asChild>
              <div className="space-y-2 text-sm text-muted-foreground">
                {dialogIntent === "loading" && <p>Checking media format usage…</p>}
                {dialogIntent === "error" && <p>{previewError}</p>}
                {dialogIntent === "delete" && (
                  <p>Are you sure you want to delete "{formatToDelete?.name}"? This action cannot be undone.</p>
                )}
                {dialogIntent === "restore" && (
                  <p>
                    Restore "{formatToDelete?.name}" to the media format lists. Store assignments that were turned off stay off until you assign the format again.
                  </p>
                )}
                {(dialogIntent === "archive" || dialogIntent === "blocked") && archivePreview && (
                  <FormatUsageWarning preview={archivePreview} archive={dialogIntent === "archive"} />
                )}
              </div>
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>{dialogIntent === "blocked" ? "Close" : "Cancel"}</AlertDialogCancel>
            {dialogIntent !== "loading" && dialogIntent !== "error" && dialogIntent !== "blocked" && (
              <AlertDialogAction
                onClick={confirmDeleteFormat}
                className={dialogIntent === "delete"
                  ? "bg-destructive text-destructive-foreground hover:bg-destructive/90"
                  : ""}
                disabled={deleteFormatMutation.isPending || archiveFormatMutation.isPending}
              >
                {dialogIntent === "delete"
                  ? (deleteFormatMutation.isPending ? "Deleting..." : "Delete")
                  : dialogIntent === "restore"
                    ? (archiveFormatMutation.isPending ? "Restoring..." : "Restore")
                    : (archiveFormatMutation.isPending ? "Archiving..." : "Archive")}
              </AlertDialogAction>
            )}
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}

export default MediaFormatsList;
